import { Mail01, Phone01, User01, AlertTriangle } from "@untitledui/icons"

import type { ApiEstimate } from "@/lib/api/types"
import type { TaskRow } from "@/lib/api/tasks"
import { readIntakeMeta } from "@/lib/api/public-consumer"
import { SURFACE_LABEL } from "@/lib/intake/intake-config"
import { formatPhone } from "@/lib/utils/phone"
import { Bone } from "./skeletons"

// Who this is, and whether we can reach them. The review's rail (can this
// customer even be followed up?) and the fix-number rail (what else do we
// have?). Facts only, no actions — the actions are in the work column.

export function CustomerPane({ estimate, row }: { estimate: ApiEstimate | undefined; row: TaskRow }) {
  if (!estimate) {
    return (
      <div className="space-y-2.5" aria-busy="true">
        <Bone className="h-4 w-24" />
        <Bone className="h-3.5 w-40" />
        <Bone className="h-3.5 w-48" />
      </div>
    )
  }
  const customer = estimate.vehicle?.customer
  const phone = customer?.phone ?? null
  const email = customer?.email ?? null
  const intake = readIntakeMeta(estimate)
  const from = intake
    ? `Came in via ${SURFACE_LABEL[intake.surface]}`
    : estimate.dealership?.name
      ? `Sent over by ${estimate.dealership.name}`
      : null
  const by = estimate.createdBy ? `${estimate.createdBy.firstName} ${estimate.createdBy.lastName}`.trim() : row.createdByName

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <User01 className="size-4 text-zinc-500" aria-hidden />
        <h3 className="text-[15px] font-semibold text-zinc-900">{row.customerName}</h3>
      </div>
      <dl className="space-y-2 text-[14px]">
        <div className="flex items-start gap-2.5">
          <Phone01 className="size-4 mt-0.5 text-zinc-400 shrink-0" aria-hidden />
          {phone ? (
            <dd>
              <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} className="text-zinc-900 tabular-nums hover:underline underline-offset-2">
                {formatPhone(phone)}
              </a>
            </dd>
          ) : (
            <dd className="inline-flex items-center gap-1.5 text-amber-700 font-medium">
              <AlertTriangle className="size-3.5" aria-hidden /> No phone on file — the texts can't run
            </dd>
          )}
        </div>
        <div className="flex items-start gap-2.5">
          <Mail01 className="size-4 mt-0.5 text-zinc-400 shrink-0" aria-hidden />
          {email ? (
            <dd className="min-w-0">
              <a href={`mailto:${email}`} className="text-zinc-900 hover:underline underline-offset-2 break-all">
                {email}
              </a>
            </dd>
          ) : (
            <dd className="text-zinc-500">No email on file</dd>
          )}
        </div>
      </dl>
      <div className="mt-3.5 pt-3.5 border-t border-zinc-200/80 text-[13px] text-zinc-600 space-y-1">
        <p className="text-zinc-900 font-medium">{row.vehicleLabel}</p>
        {estimate.vehicle?.vin && (
          <p>
            VIN <span className="tabular-nums text-zinc-700">{estimate.vehicle.vin}</span>
          </p>
        )}
        {from && <p>{from}</p>}
        {by && <p>Written up by {by}</p>}
      </div>
    </div>
  )
}
